import React from 'react'
import { type Control, Controller, type FieldError, type FieldErrors, type FieldValues, type Path } from 'react-hook-form'

import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { cn } from 'utils'

import { PayFrequencies, type PayFrequency, PayFrequencyLabels, type PayScheduleFormInputs } from '../../types/payroll'
import { FormDateInputField } from '../reusable/form/FormFields'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

export function PayFrequencyField<T extends FieldValues> ({
  control,
  error,
  fieldId,
  customOnChange,
  disabled,
}: {
  control: Control<T>
  error?: FieldError
  fieldId: Path<T>,
  customOnChange?: (value: PayFrequency, onChange: React.Dispatch<React.SetStateAction<string>>) => void
  disabled?: boolean
}) {
  return (
    <Controller
      name={fieldId}
      control={control}
      rules={{ required: true }}
      render={({ field: { onChange, value } }) => (
        <div className='space-y-2 col-span-2 sm:col-span-1'>
          <label className='label-default font-medium text-ds-text-secondary block'>
            Pay frequency
          </label>
          <TooltipWrapper
            tooltipText='You cannot change the frequency of an existing pay schedule. Please create a new schedule.'
            hidden={!disabled}
            buttonClasses={cn({
              'w-full !items-start !justify-start  focus:shadow-none !rounded-[8px]': true,
              'cursor-not-allowed': disabled,
            })}
          >

            <Select
              disabled={disabled}
              value={value} onValueChange={(newValue: PayFrequency) => {
                if (customOnChange) {
                  customOnChange(newValue, onChange)
                } else {
                  onChange(newValue)
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select pay frequency' />
              </SelectTrigger>
              <SelectContent>
                {Object.values(PayFrequencies).map((freq) => (
                  <SelectItem key={freq} value={freq}>
                    {PayFrequencyLabels[freq]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TooltipWrapper>

          {error && <p className='text-red-500 text-xs'>This field is required</p>}
        </div>
      )}
    />
  )
}

export function PayScheduleNameField<T extends FieldValues> ({
  control,
  error,
  fieldId,
}: {
  control: Control<T>
  error?: FieldError
  fieldId: Path<T>
}) {
  return (
    <div className='space-y-2 col-span-2 sm:col-span-1'>
      <label className='label-default font-medium text-ds-text-secondary block'>
        Pay schedule name
      </label>
      <Controller
        name={fieldId}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <input
            {...field}
            className='input-default w-full'
            placeholder='Name your pay schedule'
          />
        )}
      />
      {error && <p className='text-red-500 text-xs'>This field is required</p>}
    </div>
  )
}

export function PayScheduleDateFields<T extends FieldValues> ({
  control,
  errors,
  watch,
}: {
  control: Control<T>
  errors: FieldErrors<PayScheduleFormInputs>
  watch: any
}) {
  const startDate = watch('startDate')
  return (
    <div className='col-span-2 grid grid-cols-1 xs:grid-cols-2 gap-4'>
      <div className='space-y-2'>
        <label className='label-default font-medium text-ds-text-secondary block' htmlFor='startDate'>
          Start date
        </label>
        <Controller
          name={'startDate' as Path<T>}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <FormDateInputField<T>
              fieldId={'startDate' as Path<T>}
              fieldLabel=''
              watch={watch}
              onSelectDate={(date) => {
                if (date) {
                  onChange(date.toISOString().split('T')[0])
                } else {
                  onChange(undefined)
                }
              }}
              serverError={errors.startDate}
            />
          )}
        />
        {errors && errors.startDate && (<p className='text-red-500 text-xs'>This field is required</p>)}
      </div>

      <div className='space-y-2'>
        <label className='label-default font-medium text-ds-text-secondary block' htmlFor='endDate'>
          End date (optional)
        </label>
        <div className='[&>div]:w-full'>
          <Controller
            name={'endDate' as Path<T>}
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true
                if (!startDate) return true
                return new Date(value) > new Date(startDate) || 'End date must be after start date'
              }
            }}
            render={({ field: { onChange } }) => (
              <FormDateInputField<T>
                fieldId={'endDate' as Path<T>}
                fieldLabel=''
                watch={watch}
                onSelectDate={(date) => {
                  if (date) {
                    onChange(date.toISOString().split('T')[0])
                  } else {
                    onChange(undefined)
                  }
                }}
                serverError={errors.endDate}
                minDate={startDate ? new Date(startDate) : undefined}
                disabledDateFilter={(date) => startDate ? date <= new Date(startDate) : false}
                errorMessages={{
                  minDate: 'End date must be after start date',
                  maxDate: '',
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  )
}
